import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Swiper from 'swiper';
import 'swiper/dist/css/swiper.min.css';
import './Feature.swiper.css';
import { featureContent } from './Feature.content';
import { Button } from '../ui/Button';
import styles from './Feature.module.sass';
import logo from './assets/logo.svg';

gsap.registerPlugin(ScrollTrigger);

const BLUR_MAX = 10;
const TRANSITION_DURATION = 0.55;
const TRANSITION_EASE = 'power2.inOut';

function applySettledState(slideIndex, n, textRefs, imgRefs) {
  for (let i = 0; i < n; i++) {
    const textEl = textRefs.current[i];
    const imgEl = imgRefs.current[i];
    if (!textEl || !imgEl) continue;
    if (i === slideIndex) {
      gsap.set(textEl, { opacity: 1, filter: 'blur(0px)' });
      gsap.set(imgEl, { opacity: 1 });
    } else {
      gsap.set(textEl, { opacity: 0, filter: 'blur(0px)' });
      gsap.set(imgEl, { opacity: 0 });
    }
  }
}

function playTransition(to, n, textRefs, imgRefs, onComplete) {
  const tl = gsap.timeline({ onComplete });

  const dur = TRANSITION_DURATION;
  const ease = TRANSITION_EASE;

  for (let i = 0; i < n; i++) {
    const textEl = textRefs.current[i];
    const imgEl = imgRefs.current[i];
    const isTarget = i === to;

    if (textEl) {
      tl.to(
        textEl,
        {
          opacity: isTarget ? 1 : 0,
          filter: isTarget ? 'blur(0px)' : `blur(${BLUR_MAX}px)`,
          duration: dur,
          ease,
          overwrite: true,
        },
        0
      );
    }
    if (imgEl) {
      tl.to(
        imgEl,
        {
          opacity: isTarget ? 1 : 0,
          duration: dur,
          ease,
          overwrite: true,
        },
        0
      );
    }
  }

  return tl;
}

export function Feature() {
  const sectionRef = useRef(null);
  const textRefs = useRef([]);
  const imgRefs = useRef([]);
  const swiperRef = useRef(null);
  const list = featureContent.list;
  const n = list.length;
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(max-width: 900px)').matches;
    }
    return false;
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.matchMedia('(max-width: 900px)').matches);
    };
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) return;

    if (!swiperRef.current) return;

    let swiper = null;

    const timer = setTimeout(() => {
      if (swiperRef.current) {
        swiper = new Swiper(swiperRef.current, {
          direction: 'horizontal',
          loop: true,
          autoplay: {
            delay: 5000,
            disableOnInteraction: false,
          },
          speed: 600,
        });
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (swiper) swiper.destroy();
    };
  }, [isMobile]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced || n < 2 || isMobile) {
      return;
    }

    const hasRefs = featureContent.list.every((_, i) => textRefs.current[i] && imgRefs.current[i]);
    if (!hasRefs) return;

    const textRefsArray = textRefs.current;
    const imgRefsArray = imgRefs.current;

    const currentSlideRef = { current: 0 };
    let transitionTl = null;

    applySettledState(0, n, textRefs, imgRefs);

    const st = ScrollTrigger.create({
      trigger: section,
      start: 'top top',
      end: () => `+=${(n - 1) * window.innerHeight}`,
      pin: !isMobile,
      pinSpacing: !isMobile,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      refreshPriority: 5,
      onUpdate: (self) => {
        const scrollTarget = Math.min(Math.floor(self.progress * n), n - 1);

        if (scrollTarget === currentSlideRef.current) {
          return;
        }

        currentSlideRef.current = scrollTarget;

        if (transitionTl) {
          transitionTl.kill();
        }

        transitionTl = playTransition(scrollTarget, n, textRefs, imgRefs, () => {
          transitionTl = null;
        });
      },
    });

    const refresh = () => ScrollTrigger.refresh();
    refresh();
    const rafId = requestAnimationFrame(refresh);
    window.addEventListener('load', refresh);

    return () => {
      window.removeEventListener('load', refresh);
      cancelAnimationFrame(rafId);
      if (transitionTl) {
        transitionTl.kill();
      }
      const all = [];
      for (let i = 0; i < n; i++) {
        if (textRefsArray[i]) all.push(textRefsArray[i]);
        if (imgRefsArray[i]) all.push(imgRefsArray[i]);
      }
      gsap.killTweensOf(all);
      st.kill();
    };
  }, [n, list, isMobile]);

  const reduced =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (isMobile && !reduced && n >= 2) {
    return (
      <section className={styles.feature}>
        <div className={`swiper-container ${styles.feature__swiper}`} ref={swiperRef}>
          <div className="swiper-wrapper">
            {list.map((item, index) => (
              <div key={index} className="swiper-slide">
                <div className={styles.feature__top}>
                  <div className={styles.feature__logo}>
                    <img src={logo} alt="logo" />
                  </div>
                  <div className={styles.feature__titleWrapper}>
                    <h2 className={styles.feature__title}>{item.title_1}</h2>
                    {item.title_2 != null && item.title_2 !== '' && (
                      <h2 className={styles.feature__title}>{item.title_2}</h2>
                    )}
                  </div>
                  <div className={styles.feature__textWrapper}>
                    <p className={styles.feature__text}>{item.text_1}</p>
                    <p className={styles.feature__text}>{item.text_2}</p>
                  </div>
                  <div className={styles.feature__button}>
                    <Button
                      href={featureContent.buttonHref}
                      label={featureContent.buttonLabel}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="fill"
                    />
                  </div>
                </div>
                <div className={styles.feature__bottom}>
                  <img
                    src={isMobile ? item.image_m : item.image}
                    alt={[item.title_1, item.title_2].filter(Boolean).join(' ')}
                    className={styles.feature__image}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (reduced || n < 2) {
    return (
      <section className={styles.feature}>
        <div className={styles.feature__sticky}>
          {list.map((item, index) => (
            <div key={index} className={styles.feature__item}>
              <div className={styles.feature__top}>
                <div className={styles.feature__logo}>
                  <img src={logo} alt="logo" />
                </div>
                <div className={styles.feature__titleWrapper}>
                  <h2 className={styles.feature__title}>{item.title_1}</h2>
                  {item.title_2 != null && item.title_2 !== '' && (
                    <h2 className={styles.feature__title}>{item.title_2}</h2>
                  )}
                </div>
                <div className={styles.feature__textWrapper}>
                  <p className={styles.feature__text}>{item.text_1}</p>
                  <p className={styles.feature__text}>{item.text_2}</p>
                </div>
                <div className={styles.feature__button}>
                  <Button
                    href={featureContent.buttonHref}
                    label={featureContent.buttonLabel}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="fill"
                  />
                </div>
              </div>
              <div className={styles.feature__bottom}>
                <img
                  src={item.image}
                  alt={[item.title_1, item.title_2].filter(Boolean).join(' ')}
                  className={styles.feature__image}
                />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className={styles.feature}>
      <div className={styles.feature__sticky}>
        <div className={styles.feature__slides}>
          {list.map((item, index) => (
            <div key={index} className={styles.feature__slide}>
              <div
                ref={(el) => {
                  textRefs.current[index] = el;
                }}
                className={styles.feature__textBlock}
              >
                <div className={styles.feature__top}>
                  <div className={styles.feature__logo}>
                    <img src={logo} alt="logo" />
                  </div>
                  <div className={styles.feature__titleWrapper}>
                    <h2 className={styles.feature__title}>{item.title_1}</h2>
                    {item.title_2 != null && item.title_2 !== '' && (
                      <h2 className={styles.feature__title}>{item.title_2}</h2>
                    )}
                  </div>
                  <div className={styles.feature__textWrapper}>
                    <p className={styles.feature__text}>{item.text_1}</p>
                    <p className={styles.feature__text}>{item.text_2}</p>
                  </div>
                  <div className={styles.feature__button}>
                    <Button
                      href={featureContent.buttonHref}
                      label={featureContent.buttonLabel}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="fill"
                    />
                  </div>
                </div>
              </div>
              <div
                ref={(el) => {
                  imgRefs.current[index] = el;
                }}
                className={styles.feature__bottom}
              >
                <img
                  src={item.image}
                  alt={[item.title_1, item.title_2].filter(Boolean).join(' ')}
                  className={styles.feature__image}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
